<script lang="ts">
  import {
    BookOpen,
    LogIn,
    LayoutDashboard,
    ArrowRight,
    Layers,
    Lightbulb,
    Cpu,
    Gamepad2,
    Zap,
    Download,
    CheckCircle2,
    AlertTriangle,
    CircleHelp,
    ChevronRight,
    Search,
    RefreshCw,
    Shield,
    FileText,
    Sparkles
  } from 'lucide-svelte';

  let activeSection = $state('getting-started');
  let searchQuery = $state('');

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: LogIn,
      description: 'Sign in and open your first project'
    },
    {
      id: 'navigation',
      title: 'App Navigation',
      icon: LayoutDashboard,
      description: 'How the dashboard, project, and unit views fit together'
    },
    {
      id: 'workflow',
      title: 'Configuration Workflow',
      icon: ArrowRight,
      description: 'Recommended order for a new installation'
    },
    {
      id: 'features',
      title: 'Key Capabilities',
      icon: Sparkles,
      description: 'Zones, hardware, scenes, and exports'
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: CircleHelp,
      description: 'Login, loading, and validation issues'
    }
  ];

  const workflowSteps = [
    {
      step: 1,
      title: 'Create a project',
      summary: 'Name the job site and add a short description.',
      icon: LayoutDashboard,
      accent: 'bg-toyama-teal/10 text-toyama-teal border-toyama-teal/20'
    },
    {
      step: 2,
      title: 'Add units',
      summary: 'Each unit is a separate RCU configuration (floor, villa, wing, etc.).',
      icon: Layers,
      accent: 'bg-toyama-navy/5 text-toyama-navy border-toyama-navy/10'
    },
    {
      step: 3,
      title: 'Plan zones & rooms',
      summary: 'Mirror the floor plan before assigning loads.',
      icon: Layers,
      accent: 'bg-emerald-50 text-emerald-800 border-emerald-100'
    },
    {
      step: 4,
      title: 'Define loads',
      summary: 'Add dimming, relay, 1–10V, and curtain loads per room.',
      icon: Lightbulb,
      accent: 'bg-amber-50 text-amber-900 border-amber-100'
    },
    {
      step: 5,
      title: 'Configure hardware',
      summary: 'Set up RCUs, DIN modules, and channel mapping.',
      icon: Cpu,
      accent: 'bg-violet-50 text-violet-900 border-violet-100'
    },
    {
      step: 6,
      title: 'Program keypads & scenes',
      summary: 'Assign buttons, scenes, and LED feedback.',
      icon: Gamepad2,
      accent: 'bg-orange-50 text-orange-900 border-orange-100'
    },
    {
      step: 7,
      title: 'Validate & export',
      summary: 'Review warnings, then export firmware mappings and reports.',
      icon: Download,
      accent: 'bg-toyama-lime/20 text-toyama-navy border-toyama-lime/40'
    }
  ];

  const navLevels = [
    {
      title: 'Dashboard',
      detail: 'All projects you can access. Create, search, open, or delete projects.'
    },
    {
      title: 'Project view',
      detail: 'Units within the selected project. Pick a unit to configure it.'
    },
    {
      title: 'Unit workspace',
      detail: 'Phased tabs: zones & rooms, loads, hardware, keypads, scenes, reports.'
    }
  ];

  const featureCards = [
    {
      icon: Layers,
      title: 'Zones & rooms',
      body: 'Group spaces logically (floor, wing, area) before adding loads.'
    },
    {
      icon: Lightbulb,
      title: 'Loads',
      body: 'Track quantity, wattage, driver requirements, and supply materials.'
    },
    {
      icon: Cpu,
      title: 'Hardware',
      body: 'RCU addressing, DIN modules, and mapping loads to output channels.'
    },
    {
      icon: Gamepad2,
      title: 'Keypads',
      body: 'Room placement, bus addressing, and per-button programming.'
    },
    {
      icon: Zap,
      title: 'Scenes',
      body: 'Room, zone, or unit scope with fade times and load levels.'
    },
    {
      icon: FileText,
      title: 'Reports & BOM',
      body: 'Logic sheets, electrical layouts, and bill-of-materials exports.'
    }
  ];

  const troubleshooting = [
    {
      title: 'Cannot sign in',
      symptom: 'Invalid credentials or login form errors.',
      fixes: [
        'Use the username and password issued for this configuration API.',
        'After a server upgrade, hard-refresh the page (Ctrl+Shift+R).',
        'Clear site data for localhost:8000 if you previously used an older UI build.'
      ]
    },
    {
      title: 'Projects list is empty after login',
      symptom: 'Dashboard loads but shows no projects.',
      fixes: [
        'Your account may not be assigned to any project yet — ask an administrator.',
        'Staff users see all projects; collaborators only see shared projects.',
        'Create a new project from the dashboard if your role allows it.'
      ]
    },
    {
      title: 'Data failed to load',
      symptom: 'Red error banner or console 404/401 messages.',
      fixes: [
        'Sign out and sign in again to refresh your API token.',
        'Ensure the Go server is running on port 8000 (`make run`).',
        'If you changed the frontend, rebuild with `make admin-build` and restart.'
      ]
    },
    {
      title: 'Duplicate addresses',
      symptom: 'Validation warns about keypad or DIN address conflicts.',
      fixes: [
        'Check addresses within the same unit before saving.',
        'Reserve gaps for future expansion where possible.'
      ]
    },
    {
      title: 'Unmapped loads',
      symptom: 'Loads exist but are not assigned to hardware channels.',
      fixes: [
        'Open Hardware and complete channel mapping for each DIN module.',
        'Re-run validation in Reports before export.'
      ]
    },
    {
      title: 'Scene conflicts',
      symptom: 'Multiple scenes control the same load unexpectedly.',
      fixes: [
        'Review scene scope (room vs zone vs unit).',
        'Check scene load assignments and priorities.'
      ]
    }
  ];

  let filteredSections = $derived(
    sections.filter((section) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        section.title.toLowerCase().includes(q) ||
        section.description.toLowerCase().includes(q) ||
        section.id.includes(q)
      );
    })
  );

  $effect(() => {
    if (filteredSections.length && !filteredSections.some((s) => s.id === activeSection)) {
      activeSection = filteredSections[0].id;
    }
  });

  function setActiveSection(id: string) {
    activeSection = id;
  }
</script>

<div class="min-h-full bg-toyama-light-carbon">
  <!-- Hero -->
  <div class="bg-toyama-navy text-white">
    <div class="max-w-6xl mx-auto px-6 py-10">
      <div class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div class="max-w-2xl">
          <div
            class="inline-flex items-center gap-2 rounded-full bg-bg-canvas/10 px-3 py-1 text-xs font-medium text-toyama-lime mb-4"
          >
            <BookOpen class="w-3.5 h-3.5" />
            Help & Guide
          </div>
          <h1 class="text-3xl font-display font-bold text-white mb-3">
            Toyama RCU Configuration Tool
          </h1>
          <p class="text-sm md:text-base text-white/75 leading-relaxed">
            Plan zones, map hardware, program keypads and scenes, and export firmware-ready
            configurations — all from one workspace.
          </p>
        </div>

        <div class="w-full lg:max-w-sm">
          <label class="sr-only" for="guide-search">Search guide</label>
          <div class="relative">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
            <input
              id="guide-search"
              type="search"
              bind:value={searchQuery}
              placeholder="Search topics..."
              class="w-full rounded-lg border border-white/15 bg-bg-canvas/10 py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-toyama-lime/60"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="max-w-6xl mx-auto px-6 py-8">
    <div class="flex flex-col lg:flex-row gap-8">
      <!-- Sidebar -->
      <aside class="lg:w-72 shrink-0">
        <div class="lg:sticky lg:top-6 space-y-2">
          {#each filteredSections as section}
            {@const Icon = section.icon}
            <button
              type="button"
              onclick={() => setActiveSection(section.id)}
              class="w-full text-left rounded-xl border px-4 py-3 transition-all {activeSection ===
              section.id
                ? 'border-toyama-teal bg-bg-canvas shadow-sm ring-1 ring-toyama-teal/15'
                : 'border-transparent bg-bg-canvas/70 hover:bg-bg-canvas hover:border-toyama-cloudy-gray'}"
            >
              <div class="flex items-start gap-3">
                <div
                  class="mt-0.5 rounded-lg p-2 {activeSection === section.id
                    ? 'bg-toyama-teal/10 text-toyama-teal'
                    : 'bg-toyama-light-carbon text-toyama-dark-gray'}"
                >
                  <Icon class="w-4 h-4" />
                </div>
                <div>
                  <div class="font-medium text-toyama-navy">{section.title}</div>
                  <div class="text-xs text-toyama-dark-gray mt-0.5 leading-relaxed">
                    {section.description}
                  </div>
                </div>
              </div>
            </button>
          {:else}
            <div
              class="rounded-xl border border-toyama-cloudy-gray bg-bg-canvas p-4 text-sm text-toyama-dark-gray"
            >
              No topics match your search.
            </div>
          {/each}
        </div>
      </aside>

      <!-- Content -->
      <div class="flex-1 min-w-0 space-y-6">
        {#if activeSection === 'getting-started'}
          <section
            class="rounded-2xl border border-toyama-cloudy-gray bg-bg-canvas p-6 md:p-8 shadow-sm"
          >
            <h2 class="text-2xl font-display font-semibold text-toyama-navy mb-2">
              Getting started
            </h2>
            <p class="text-sm text-toyama-dark-gray mb-8 max-w-3xl">
              The tool runs in your browser and talks to the configuration API on the same server.
              You need valid API credentials before you can view or edit projects.
            </p>

            <div class="grid gap-4 md:grid-cols-3 mb-8">
              <div class="rounded-xl border border-toyama-cloudy-gray p-5">
                <div
                  class="w-10 h-10 rounded-lg bg-toyama-teal/10 text-toyama-teal flex items-center justify-center mb-3"
                >
                  <span class="font-display font-bold">1</span>
                </div>
                <h3 class="font-medium text-toyama-navy mb-1">Open the app</h3>
                <p class="text-sm text-toyama-dark-gray">
                  Go to <span class="font-mono text-xs bg-toyama-light-carbon px-1.5 py-0.5 rounded"
                    >http://localhost:8000</span
                  >
                  in development, or your deployed server URL.
                </p>
              </div>
              <div class="rounded-xl border border-toyama-cloudy-gray p-5">
                <div
                  class="w-10 h-10 rounded-lg bg-toyama-teal/10 text-toyama-teal flex items-center justify-center mb-3"
                >
                  <span class="font-display font-bold">2</span>
                </div>
                <h3 class="font-medium text-toyama-navy mb-1">Sign in</h3>
                <p class="text-sm text-toyama-dark-gray">
                  Enter your API username and password. A token is stored locally so you stay signed
                  in.
                </p>
              </div>
              <div class="rounded-xl border border-toyama-cloudy-gray p-5">
                <div
                  class="w-10 h-10 rounded-lg bg-toyama-teal/10 text-toyama-teal flex items-center justify-center mb-3"
                >
                  <span class="font-display font-bold">3</span>
                </div>
                <h3 class="font-medium text-toyama-navy mb-1">Open a project</h3>
                <p class="text-sm text-toyama-dark-gray">
                  Pick an existing project or create one, then select a unit to begin configuration.
                </p>
              </div>
            </div>

            <div class="rounded-xl bg-toyama-navy text-white p-5 md:p-6">
              <div class="flex items-start gap-3">
                <Shield class="w-5 h-5 text-toyama-lime shrink-0 mt-0.5" />
                <div>
                  <h3 class="font-medium mb-2">Access & permissions</h3>
                  <ul class="text-sm text-white/80 space-y-2">
                    <li>
                      • <strong class="text-white">Owner</strong> — full control over the project
                    </li>
                    <li>
                      • <strong class="text-white">Editor</strong> — configure hardware and structure
                      (when allowed)
                    </li>
                    <li>
                      • <strong class="text-white">Scene editor</strong> — scenes and key actions only
                    </li>
                    <li>• <strong class="text-white">Viewer</strong> — read-only access</li>
                    <li>
                      • <strong class="text-white">Staff</strong> — sees all projects on the server
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        {/if}

        {#if activeSection === 'navigation'}
          <section
            class="rounded-2xl border border-toyama-cloudy-gray bg-bg-canvas p-6 md:p-8 shadow-sm"
          >
            <h2 class="text-2xl font-display font-semibold text-toyama-navy mb-2">
              App navigation
            </h2>
            <p class="text-sm text-toyama-dark-gray mb-8">
              The sidebar and breadcrumb trail show where you are. Configuration always flows from
              project → unit → phase tabs.
            </p>

            <div class="space-y-4 mb-8">
              {#each navLevels as level, index}
                <div class="flex gap-4">
                  <div class="flex flex-col items-center">
                    <div
                      class="w-8 h-8 rounded-full bg-toyama-teal text-white text-sm font-medium flex items-center justify-center"
                    >
                      {index + 1}
                    </div>
                    {#if index < navLevels.length - 1}
                      <div class="w-px flex-1 bg-toyama-cloudy-gray my-2 min-h-[2rem]"></div>
                    {/if}
                  </div>
                  <div class="pb-4">
                    <h3 class="font-medium text-toyama-navy">{level.title}</h3>
                    <p class="text-sm text-toyama-dark-gray mt-1">{level.detail}</p>
                  </div>
                </div>
              {/each}
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <div
                class="rounded-xl border border-dashed border-toyama-teal/30 bg-toyama-teal/5 p-5"
              >
                <h3 class="font-medium text-toyama-navy mb-2">Unit phase tabs</h3>
                <ul class="text-sm text-toyama-dark-gray space-y-1.5">
                  <li class="flex items-center gap-2">
                    <ChevronRight class="w-4 h-4 text-toyama-teal" /> Building tree — zones & rooms
                  </li>
                  <li class="flex items-center gap-2">
                    <ChevronRight class="w-4 h-4 text-toyama-teal" /> Loads — fixtures and drivers
                  </li>
                  <li class="flex items-center gap-2">
                    <ChevronRight class="w-4 h-4 text-toyama-teal" /> Hardware — RCU & DIN modules
                  </li>
                  <li class="flex items-center gap-2">
                    <ChevronRight class="w-4 h-4 text-toyama-teal" /> Keypads — addressing & keys
                  </li>
                  <li class="flex items-center gap-2">
                    <ChevronRight class="w-4 h-4 text-toyama-teal" /> Scenes — automation programming
                  </li>
                  <li class="flex items-center gap-2">
                    <ChevronRight class="w-4 h-4 text-toyama-teal" /> Reports — validation & exports
                  </li>
                </ul>
              </div>
              <div class="rounded-xl border border-toyama-cloudy-gray p-5">
                <h3 class="font-medium text-toyama-navy mb-2">Quick tips</h3>
                <ul class="text-sm text-toyama-dark-gray space-y-2">
                  <li>Use the menu button to show or hide the sidebar.</li>
                  <li>Breadcrumbs jump back to Dashboard or the project view.</li>
                  <li>Progress indicators on the unit workspace show which phases are complete.</li>
                  <li>Save frequently — most changes sync to the API immediately.</li>
                </ul>
              </div>
            </div>
          </section>
        {/if}

        {#if activeSection === 'workflow'}
          <section
            class="rounded-2xl border border-toyama-cloudy-gray bg-bg-canvas p-6 md:p-8 shadow-sm"
          >
            <h2 class="text-2xl font-display font-semibold text-toyama-navy mb-2">
              Configuration workflow
            </h2>
            <p class="text-sm text-toyama-dark-gray mb-8">
              Follow this order for a clean configuration. Skipping steps early makes channel
              mapping and scene programming harder later.
            </p>

            <div class="space-y-3">
              {#each workflowSteps as item}
                {@const Icon = item.icon}
                <div
                  class="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border p-4 {item.accent}"
                >
                  <div class="flex items-center gap-3 sm:w-56 shrink-0">
                    <div
                      class="w-9 h-9 rounded-lg bg-bg-canvas/80 flex items-center justify-center font-display font-bold text-sm"
                    >
                      {item.step}
                    </div>
                    <Icon class="w-5 h-5 opacity-80" />
                    <span class="font-medium">{item.title}</span>
                  </div>
                  <p class="text-sm opacity-90">{item.summary}</p>
                </div>
              {/each}
            </div>

            <div
              class="mt-8 rounded-xl bg-toyama-light-carbon border border-toyama-cloudy-gray p-5"
            >
              <div class="flex items-start gap-3">
                <CheckCircle2 class="w-5 h-5 text-toyama-teal shrink-0 mt-0.5" />
                <p class="text-sm text-toyama-heavy-carbon">
                  <strong class="text-toyama-navy">Pro tip:</strong> Start with a small pilot unit to
                  learn the workflow, then duplicate or clone units for repeated floor plates.
                </p>
              </div>
            </div>
          </section>
        {/if}

        {#if activeSection === 'features'}
          <section
            class="rounded-2xl border border-toyama-cloudy-gray bg-bg-canvas p-6 md:p-8 shadow-sm"
          >
            <h2 class="text-2xl font-display font-semibold text-toyama-navy mb-2">
              Key capabilities
            </h2>
            <p class="text-sm text-toyama-dark-gray mb-8">
              Each area of the unit workspace focuses on one part of the system design.
            </p>

            <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {#each featureCards as card}
                {@const Icon = card.icon}
                <div
                  class="rounded-xl border border-toyama-cloudy-gray p-5 hover:border-toyama-teal/30 hover:shadow-sm transition-all"
                >
                  <div
                    class="w-10 h-10 rounded-lg bg-toyama-teal/10 text-toyama-teal flex items-center justify-center mb-3"
                  >
                    <Icon class="w-5 h-5" />
                  </div>
                  <h3 class="font-medium text-toyama-navy mb-1">{card.title}</h3>
                  <p class="text-sm text-toyama-dark-gray leading-relaxed">{card.body}</p>
                </div>
              {/each}
            </div>

            <div class="mt-8 grid gap-4 md:grid-cols-2">
              <div class="rounded-xl border border-toyama-cloudy-gray p-5">
                <h3 class="font-medium text-toyama-navy mb-2 flex items-center gap-2">
                  <Download class="w-4 h-4 text-toyama-teal" />
                  Firmware export
                </h3>
                <p class="text-sm text-toyama-dark-gray">
                  Export v3 JSON, v2 text, C headers, or a ZIP bundle from the unit reports area
                  once validation passes.
                </p>
              </div>
              <div class="rounded-xl border border-toyama-cloudy-gray p-5">
                <h3 class="font-medium text-toyama-navy mb-2 flex items-center gap-2">
                  <RefreshCw class="w-4 h-4 text-toyama-teal" />
                  LED logic sync
                </h3>
                <p class="text-sm text-toyama-dark-gray">
                  Preview auto-generated LED mappings, sync them to the database, and override with
                  custom mappings when needed.
                </p>
              </div>
            </div>
          </section>
        {/if}

        {#if activeSection === 'troubleshooting'}
          <section
            class="rounded-2xl border border-toyama-cloudy-gray bg-bg-canvas p-6 md:p-8 shadow-sm"
          >
            <h2 class="text-2xl font-display font-semibold text-toyama-navy mb-2">
              Troubleshooting
            </h2>
            <p class="text-sm text-toyama-dark-gray mb-8">
              Common issues and what to try first. Check the red error banner at the top of the app
              for API messages after login.
            </p>

            <div class="space-y-4">
              {#each troubleshooting as item}
                <details class="group rounded-xl border border-toyama-cloudy-gray overflow-hidden">
                  <summary
                    class="flex cursor-pointer items-center justify-between gap-4 bg-toyama-light-carbon/60 px-5 py-4 hover:bg-toyama-light-carbon"
                  >
                    <div>
                      <h3 class="font-medium text-toyama-navy">{item.title}</h3>
                      <p class="text-xs text-toyama-dark-gray mt-0.5">{item.symptom}</p>
                    </div>
                    <ChevronRight
                      class="w-4 h-4 text-toyama-dark-gray transition-transform group-open:rotate-90"
                    />
                  </summary>
                  <div class="px-5 py-4 border-t border-toyama-cloudy-gray">
                    <ul class="space-y-2">
                      {#each item.fixes as fix}
                        <li class="flex items-start gap-2 text-sm text-toyama-heavy-carbon">
                          <CheckCircle2 class="w-4 h-4 text-toyama-teal shrink-0 mt-0.5" />
                          <span>{fix}</span>
                        </li>
                      {/each}
                    </ul>
                  </div>
                </details>
              {/each}
            </div>

            <div class="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-5">
              <div class="flex items-start gap-3">
                <AlertTriangle class="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h3 class="font-medium text-amber-950 mb-1">Still stuck?</h3>
                  <p class="text-sm text-amber-900/80">
                    Note the project and unit name, the exact error message, and contact your Toyama
                    administrator. Include a browser console screenshot if API requests are failing.
                  </p>
                </div>
              </div>
            </div>
          </section>
        {/if}
      </div>
    </div>
  </div>
</div>
